import {
    Component,
    computed,
    forwardRef,
    input,
    InputSignal,
    signal,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    Signal
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    Validator,
    ValidationErrors,
    FormBuilder,
    FormControl,
    FormArray,
    FormsModule,
    ReactiveFormsModule,
    AbstractControl,
    FormGroup,
    Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TextValueAccessor } from '@shui/angular';
import { MatDialog } from '@angular/material/dialog';
import { ShScdPickerDialogComponent } from '../sh-scd-picker-dialog/sh-scd-picker-dialog.component';
import { filter, tap } from 'rxjs';
import { SCDUser } from '@shs-digi-care-monorepo/interfaces';
import { UserState } from '../../states';
import { select } from '@ngxs/store';
import { ShAdditionalEmailsLayout } from './sh-additional-emails.model';

/**
 * Additional emails component with SCD picker integration.
 * Each email input row has a user-lookup button that opens the SCD picker dialog
 * and fills the email field with the selected user's email.
 */
@Component({
    selector: 'sh-additional-emails',
    templateUrl: './sh-additional-emails.component.html',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShAdditionalEmailsComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ShAdditionalEmailsComponent),
            multi: true
        }
    ],
    imports: [FormsModule, ReactiveFormsModule, CommonModule, TextValueAccessor],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShAdditionalEmailsComponent implements ControlValueAccessor, Validator {
    private readonly fb = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    public label: InputSignal<string> = input<string>('');
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public addButtonLabel: InputSignal<string> = input<string>('Add');
    public errorMessage: InputSignal<string> = input<string>('Please enter a valid email address');
    public showScdPicker: InputSignal<boolean> = input(true);
    public noBorder: InputSignal<boolean> = input<boolean>(false);
    /**
     * `vertical` (default) renders the add trigger as a labeled button below the inputs.
     * `horizontal` renders it as a borderless plus icon to the right of the last input.
     */
    public layout: InputSignal<ShAdditionalEmailsLayout> = input<ShAdditionalEmailsLayout>('vertical');

    protected isInternalUser: Signal<boolean> = select(UserState.isInternal);

    private inputFormArray: FormArray = this.fb.array([new FormControl('', [Validators.email])]);
    public form: FormGroup = this.fb.group({
        inputs: this.inputFormArray
    });

    private onChange: (_: unknown) => void = () => {};

    private onTouched: () => void = () => {};

    private onValidatorChange: () => void = () => {};

    public get controls(): AbstractControl[] {
        return this.inputFormArray.controls;
    }

    public handleOnBlur(): void {
        this.onTouched();
    }

    public addInput(): void {
        this.inputFormArray.push(new FormControl('', [Validators.email]));
        this.onChange(this.inputFormArray.value);
        this.onTouched();
        this.onValidatorChange();
    }

    public removeInput(index: number): void {
        if (this.inputFormArray.length > 1) {
            this.inputFormArray.removeAt(index);
            this.onChange(this.inputFormArray.value);
            this.onTouched();
            this.onValidatorChange();
        }
    }

    public openScdPicker(index: number): void {
        if (this.isDisabledInternal()) {
            return;
        }

        const dialogRef = this.dialog.open(ShScdPickerDialogComponent, {
            width: '700px',
            panelClass: 'scd-picker-dialog-panel'
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((res) => !!res && !!res.mail),
                tap((result: SCDUser | undefined) => {
                    const control = this.inputFormArray.at(index);
                    if (control) {
                        control.setValue(result?.mail);
                        this.onChange(this.inputFormArray.value);
                        this.onTouched();
                    }
                })
            )
            .subscribe();
    }

    public writeValue(value: string[]): void {
        while (this.inputFormArray.length !== 0) {
            this.inputFormArray.removeAt(0, { emitEvent: false });
        }
        if (value && Array.isArray(value) && value.length > 0) {
            value.forEach((item: string) => {
                this.inputFormArray.push(new FormControl(item, [Validators.email]), { emitEvent: false });
            });
        } else {
            this.inputFormArray.push(new FormControl('', [Validators.email]), { emitEvent: false });
        }

        // FormControls pushed after the parent FormArray was disabled are not disabled
        // automatically. Re-apply the disabled state so values written by patchValue()
        // after setDisabledState(true) do not silently re-enable the inputs.
        if (this.disabledByCva()) {
            this.inputFormArray.disable({ emitEvent: false });
        }
    }

    public registerOnChange(fn: (_: unknown) => void): void {
        this.onChange = fn;
        this.inputFormArray.valueChanges
            .pipe(
                tap((value) => {
                    fn(value);
                    this.onValidatorChange();
                })
            )
            .subscribe();
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabledByCva.set(isDisabled);
        if (isDisabled) {
            this.inputFormArray.disable({ emitEvent: false });
        } else {
            this.inputFormArray.enable({ emitEvent: false });
        }
    }

    private disabledByCva = signal(false);

    protected isDisabledInternal = computed(() => this.disabled() || this.disabledByCva());

    protected hasError(control: AbstractControl): boolean {
        return control.invalid && control.touched;
    }

    public validate(_control: AbstractControl): ValidationErrors | null {
        const hasInvalidEmail = this.inputFormArray.controls.some((ctrl) => ctrl.value && ctrl.invalid);
        return hasInvalidEmail ? { invalidEmail: true } : null;
    }

    public registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }
}
